import { useState } from 'react';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { HistoryList } from "../../../components/HistoryList";
import { formatDate } from "../../../utils/DateUtils";
import { formatCurrency } from '../../../utils/CurrencyUtils';
import { getStudentName } from '../../../utils/StringUtils';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { MdDownload, MdCheckCircle, MdNotificationsActive } from 'react-icons/md';
import { useFinancialDashboard } from '../hooks/useFinancialDashboard';
import ChartWidget from '../../../components/reusables/chartWidget';
import Registry from './registry/Registry';
import RegistryInfo from '../../../components/registryInfo';
import RegistryOrdersInfo from '../../../components/registryOrdersInfo';
import { ConfirmAlternativePaymentModal } from '../../orders/components/confirmAlternativePaymentModal';
import { useAuth } from '../../../auth/AuthenticationWrapper';

const ResponsiveGridLayout = WidthProvider(Responsive);

const defaultLayouts = {
    lg: [
        { i: 'cashflow', x: 0, y: 0, w: 1, h: 2, minW: 1, minH: 2 },
        { i: 'chart_payment', x: 1, y: 0, w: 1, h: 2 },
        { i: 'chart_product', x: 2, y: 0, w: 1, h: 2 },
        { i: 'debtors', x: 0, y: 2, w: 1, h: 3 },
        { i: 'payments_received', x: 1, y: 2, w: 1, h: 3 },
        { i: 'orders_to_receive', x: 2, y: 2, w: 1, h: 3 },
        { i: 'registry_history', x: 0, y: 5, w: 1, h: 3 },
        { i: 'registry_orders', x: 1, y: 5, w: 2, h: 3 },
    ],
    md: [
        { i: 'cashflow', x: 0, y: 0, w: 1, h: 2 },
        { i: 'chart_payment', x: 1, y: 0, w: 1, h: 2 },
        { i: 'chart_product', x: 0, y: 2, w: 2, h: 2 },
        { i: 'debtors', x: 0, y: 4, w: 1, h: 3 },
        { i: 'payments_received', x: 1, y: 4, w: 1, h: 3 },
        { i: 'orders_to_receive', x: 0, y: 7, w: 2, h: 3 },
        { i: 'registry_history', x: 0, y: 10, w: 1, h: 3 },
        { i: 'registry_orders', x: 1, y: 10, w: 1, h: 3 },
    ],
    xs: [
        { i: 'cashflow', x: 0, y: 0, w: 1, h: 2 },
        { i: 'chart_payment', x: 0, y: 2, w: 1, h: 2 },
        { i: 'chart_product', x: 0, y: 4, w: 1, h: 2 },
        { i: 'orders_to_receive', x: 0, y: 6, w: 1, h: 3 },
        { i: 'debtors', x: 0, y: 9, w: 1, h: 3 },
        { i: 'payments_received', x: 0, y: 12, w: 1, h: 3 },
        { i: 'registry_history', x: 0, y: 15, w: 1, h: 2 },
        { i: 'registry_orders', x: 0, y: 17, w: 1, h: 2 },
    ]
};

const FinancialDashboard = () => {
    const { hasPermission } = useAuth();
    const canNotify = hasPermission("financial", "send_notifications");
    const canSetReceivedPayments = hasPermission("financial", "set_received_payment");
    const canConciliatePayments = hasPermission("financial", "concilate_payments");
    const { data, filters, actions } = useFinancialDashboard();
    const [selectedRegistry, setSelectedRegistry] = useState(null);
    const [showRegistryInfo, setShowRegistryInfo] = useState(false);

    const [selectedRegistryOrder, setSelectedRegistryOrder] = useState(null);
    const [showRegistryOrderModal, setShowRegistryOrderModal] = useState(false);

    const [orderForPaymentConfirmation, setOrderForPaymentConfirmation] = useState(null);

    const handleRegistryClick = (item) => {
        setSelectedRegistry(item);
        setShowRegistryInfo(true);
    };

    const handleRegistryOrderClick = (item) => {
        setSelectedRegistryOrder(item);
        setShowRegistryOrderModal(true);
    };

    const [layouts, setLayouts] = useState(() => {
        const saved = localStorage.getItem("financial_dashboard_layout_v4");
        return saved ? JSON.parse(saved) : defaultLayouts;
    });

    const onLayoutChange = (currentLayout, allLayouts) => {
        setLayouts(allLayouts);
        localStorage.setItem("financial_dashboard_layout_v4", JSON.stringify(allLayouts));
    };

    const renderPaymentItem = (item) => (
        <div className="flex justify-between items-center text-sm p-1">
            <div>
                <p className="font-bold text-gray-700">{getStudentName(item.customer_id)}</p>
                <p className="text-xs text-gray-500">{formatDate(item.transactionDate)}</p>
            </div>
            <div className="text-right">
                <p className="font-bold text-green-600">{formatCurrency(item.value)}</p>
                <div className="flex gap-2 mt-1 justify-end">
                    <button onClick={(e) => { e.stopPropagation(); actions.downloadReceipt(item.observations) }} className="text-blue-500 hover:bg-blue-50 p-1 rounded disabled:opacity-50" disabled={!canConciliatePayments}><MdDownload /></button>
                    <button onClick={(e) => { e.stopPropagation(); actions.confirmReceivedPayment(item) }} className="text-green-500 hover:bg-green-50 p-1 rounded disabled:opacity-50" disabled={!canConciliatePayments}><MdCheckCircle /></button>
                </div>
            </div>
        </div>
    );

    const renderOrderToReceive = (order) => (
        <div className="flex justify-between items-center text-sm p-1 w-full">
            <div>
                <p className="font-bold text-gray-700">#{order.order_id}</p>
                <p className="text-xs text-gray-500">{order.client_id.split('/')[1]}</p>
            </div>
            <div className="text-right">
                <p className="font-bold text-yellow-600">{formatCurrency(order.total)}</p>
                <button onClick={(e) => { e.stopPropagation(); setOrderForPaymentConfirmation(order); }} className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded mt-1 hover:bg-green-200 font-bold disabled:opacity-50" disabled={!canSetReceivedPayments}>
                    Confirmar
                </button>
            </div>
        </div>
    );

    const renderRegistryRow = (item) => (
        <div className="flex justify-between text-sm items-center border-b border-slate-50 py-2 last:border-0 hover:bg-slate-50 transition-colors cursor-pointer px-2">
            <div className='flex flex-col'>
                <span className='font-semibold text-gray-600'>Abertura</span>
                <span className='text-xs text-gray-400'>{formatDate(item.opening_time)}</span>
            </div>
            <div className='flex flex-col text-right'>
                <span className='font-semibold text-gray-600'>Fechamento</span>
                <span className="font-bold text-green-600">{formatCurrency(item.final_value)}</span>
            </div>
        </div>
    );

    const renderRegistryOrdersRow = (item) => (
        <div className="flex justify-between text-sm items-center border-b border-slate-50 py-2 last:border-0 hover:bg-slate-50 transition-colors cursor-pointer px-2">
            <div className='flex flex-col'>
                <span className='font-bold text-gray-700'>Caixa Fechado</span>
                <span className='text-xs text-gray-400'>{formatDate(item.openingTime)}</span>
            </div>
            <div className='flex flex-col text-right'>
                <span className='text-xs font-semibold text-gray-500'>Data Fechamento</span>
                <span className="text-sm font-medium text-custom_blue">{formatDate(item.closingTime)}</span>
            </div>
        </div>
    );

    return (
        <div className="flex flex-col min-h-screen bg-slate-50 font-poppins">
            <div>
                <ResponsiveGridLayout
                    className="layout"
                    layouts={layouts}
                    breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                    cols={{ lg: 3, md: 2, sm: 2, xs: 1, xxs: 1 }}
                    rowHeight={150}
                    onLayoutChange={onLayoutChange}
                    draggableHandle=".drag-handle"
                    margin={[16, 16]}
                >
                    {/* 1. FLUXO DE CAIXA */}
                    <div key="cashflow" className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col">
                        <div className="drag-handle p-3 bg-slate-100 border-b border-slate-200 cursor-move font-bold text-gray-600 text-sm">Fluxo de Caixa (Hoje)</div>
                        <div className="flex-1 overflow-y-auto p-2">
                            <Registry handleEditClick={() => { }} onTransactionUpdate={actions.updateRegistryTransaction} transaction={data.registryTransaction} />
                        </div>
                    </div>

                    {/* 2. GRÁFICO PAGAMENTOS */}
                    <div key="chart_payment">
                        <ChartWidget
                            title={`Métodos de Pagamento`}
                            data={data.paymentStats}
                            startDate={data.chartFilters.payment.start}
                            endDate={data.chartFilters.payment.end}
                            onDateChange={(field, val) => actions.updateChartDate('payment', field, val)}
                            onSearch={actions.searchPaymentChart}
                            backgroundColor="rgba(59, 130, 246, 0.5)" borderColor="rgba(59, 130, 246, 1)"
                        />
                    </div>

                    {/* 3. GRÁFICO PRODUTOS */}
                    <div key="chart_product">
                        <ChartWidget
                            title={`Produtos Mais Vendidos`}
                            data={data.productStats}
                            startDate={data.chartFilters.product.start}
                            endDate={data.chartFilters.product.end}
                            onDateChange={(field, val) => actions.updateChartDate('product', field, val)}
                            onSearch={actions.searchProductChart}
                            backgroundColor="rgba(16, 185, 129, 0.5)" borderColor="rgba(16, 185, 129, 1)"
                        />
                    </div>

                    {/* 4. DEVEDORES */}
                    <div key="debtors" className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col">
                        <div className="drag-handle p-3 bg-red-50 border-b border-red-100 cursor-move font-bold text-red-800 text-sm flex justify-between">
                            <span>Clientes Devedores <span className="font-normal text-xs">({data.debtors.length})</span></span>
                            <span className="bg-white px-2 rounded text-xs border border-red-200 font-bold">
                                {formatCurrency(data.debtors.reduce((sum, c) => sum + c.credit, 0))}
                            </span>
                        </div>
                        <div className="flex-1 overflow-y-auto p-2">
                            {data.debtors.length === 0 ? <p className="text-center text-gray-400 mt-10">Nenhum devedor.</p> :
                                data.debtors.map((client, idx) => (
                                    <div key={idx} className="flex justify-between items-center p-3 mb-2 bg-white border border-slate-100 rounded-lg shadow-sm">
                                        <div>
                                            <p className="font-bold text-gray-700 text-sm">{client.name}</p>
                                            <p className="text-xs text-red-500 font-bold">{formatCurrency(client.credit)}</p>
                                        </div>
                                        <button onClick={() => actions.notifyClient(client)} disabled={!canNotify || client.notified} className={`p-2 rounded-full ${client.notified ? 'text-green-500 bg-green-50' : 'text-red-500 bg-red-50 hover:bg-red-100 disabled:opacity-50'}`}>
                                            {client.notified ? <MdCheckCircle /> : <MdNotificationsActive />}
                                        </button>
                                    </div>
                                ))
                            }
                        </div>
                    </div>

                    {/* 5. PAGAMENTOS RECEBIDOS */}
                    <div key="payments_received" className="bg-white rounded-2xl shadow-sm border border-slate-200 flex flex-col overflow-hidden">
                        <div className="drag-handle p-3 bg-blue-50 border-b border-blue-100 cursor-move font-bold text-blue-800 text-sm flex justify-between">
                            <span>Conciliação de Recebimentos</span>
                            <span className="bg-white px-2 rounded-full text-xs border border-blue-200">{data.paymentsReceived.length}</span>
                        </div>
                        <div className="flex-1 overflow-hidden p-2">
                            <HistoryList title="" data={data.paymentsReceived} renderItem={renderPaymentItem} className="h-full" />
                        </div>
                    </div>

                    {/* 6. PEDIDOS A RECEBER */}
                    <div key="orders_to_receive" className="bg-white rounded-2xl shadow-sm border border-slate-200 flex flex-col overflow-hidden">
                        <div className="drag-handle p-3 bg-yellow-50 border-b border-yellow-100 cursor-move font-bold text-yellow-800 text-sm flex justify-between">
                            <span>Pedidos a Receber (Balcão)</span>
                            <span className="bg-white px-2 rounded-full text-xs border border-yellow-200">{data.ordersInPayment.length}</span>
                        </div>
                        <div className="flex-1 overflow-hidden p-2">
                            <HistoryList title="" data={data.ordersInPayment} renderItem={renderOrderToReceive} className="h-full" />
                        </div>
                    </div>

                    {/* 7. HISTÓRICO DE FECHAMENTO */}
                    <div key="registry_history" className="bg-white rounded-2xl shadow-sm border border-slate-200 flex flex-col overflow-hidden">
                        <div className="drag-handle p-3 bg-slate-100 border-b border-slate-200 cursor-move font-bold text-gray-600 text-sm">Histórico de Fechamento</div>
                        <div className="flex-1 overflow-hidden p-2">
                            <HistoryList
                                title=""
                                data={data.registryHistory.map(i => ({ ...i, transactionDate: i.closing_time }))}
                                renderItem={renderRegistryRow}
                                onItemClick={handleRegistryClick}
                                className="h-full"
                            />
                        </div>
                    </div>

                    {/* 8. HISTÓRICO DE PEDIDOS POR CAIXA */}
                    <div key="registry_orders" className="bg-white rounded-2xl shadow-sm border border-slate-200 flex flex-col overflow-hidden">
                        <div className="drag-handle p-3 bg-indigo-50 border-b border-indigo-100 cursor-move font-bold text-indigo-800 text-sm">
                            Histórico de Pedidos por Caixa
                        </div>
                        <div className="flex-1 overflow-hidden p-2">
                            <HistoryList
                                title=""
                                data={data.registryOrdersHistory.map(i => ({ ...i, transactionDate: i.closingTime }))}
                                renderItem={renderRegistryOrdersRow}
                                onItemClick={handleRegistryOrderClick}
                                className="h-full"
                            />
                        </div>
                    </div>

                </ResponsiveGridLayout>
            </div>

            {selectedRegistry && (
                <RegistryInfo
                    registry={selectedRegistry}
                    showRegistryInfoModal={showRegistryInfo}
                    handleCancelClick={() => setShowRegistryInfo(false)}
                />
            )}

            {selectedRegistryOrder && (
                <RegistryOrdersInfo
                    registryOrders={selectedRegistryOrder}
                    showRegistryOrdersInfoModal={showRegistryOrderModal}
                    handleCancelClick={() => setShowRegistryOrderModal(false)}
                />
            )}

            <ConfirmAlternativePaymentModal
                isOpen={!!orderForPaymentConfirmation}
                order={orderForPaymentConfirmation}
                onClose={() => setOrderForPaymentConfirmation(null)}
                onSuccess={(orderId) => {
                    actions.removeOrderFromPaymentList(orderId);
                    setOrderForPaymentConfirmation(null);
                }}
            />

        </div>
    );
};

export default FinancialDashboard;